<template>
  <div class="occ-tl">
    <div v-if="loading" class="text-center py-3">
      <b-spinner small variant="primary" />
    </div>

    <div v-else-if="!logs.length" class="occ-tl-empty">
      <feather-icon icon="ClockIcon" size="22" class="mb-1" />
      <p class="mb-0">Nenhum evento registrado.</p>
    </div>

    <div v-else class="occ-tl-list">
      <div
        v-for="(log, idx) in logs"
        :key="log.id || idx"
        class="occ-tl-item"
      >
        <!-- Trilho: dot + conector -->
        <div class="occ-tl-rail">
          <div
            class="occ-tl-dot"
            :class="{ 'occ-tl-dot--current': isCurrentStep(idx), 'occ-tl-dot--destination': isDestination(log) }"
            :style="dotStyle(log, isCurrentStep(idx))"
          />
          <div
            v-if="idx < logs.length - 1"
            class="occ-tl-connector"
            :style="{ borderLeftColor: entryColor(log) + '35' }"
          />
        </div>

        <!-- Conteúdo -->
        <div class="occ-tl-body" :class="{ 'occ-tl-body--last': idx === logs.length - 1 }">
          <div class="occ-tl-row">
            <span class="occ-tl-step" :style="{ color: entryColor(log) }">
              <feather-icon v-if="isDestination(log)" icon="MapPinIcon" size="11" class="mr-25" />
              <feather-icon v-else-if="isTechnicalDecision(log)" :icon="decisionIcon(log.decision_fixed_key)" size="11" class="mr-25" />
              <feather-icon v-else-if="isRedirect(log)" icon="ShuffleIcon" size="11" class="mr-25" />
              <feather-icon v-else-if="isAutoClassification(log)" icon="FlagIcon" size="11" class="mr-25" />
              {{ entryLabel(log) }}
              <span
                v-if="isTechnicalDecision(log) && log.team_request_status"
                class="occ-tl-state"
                :class="'occ-tl-state--' + requestStateKey(log.team_request_status)"
              >{{ requestStateLabel(log.team_request_status) }}</span>
            </span>
            <span class="occ-tl-date">{{ formatDate(log.created_at) }}</span>
          </div>

          <!-- Badge de duração (só faz sentido pra etapas, destino/decisão/redirect são pontuais) -->
          <!-- A finalização é o ponto final: não há tempo "nesta etapa" para contar. -->
          <div v-if="!isPunctualEntry(log) && !isFinalStep(log)" class="occ-tl-duration-row mb-1">
            <span
              v-if="!isCurrentStep(idx)"
              class="occ-tl-duration occ-tl-duration--done"
            >
              <feather-icon icon="ClockIcon" size="10" class="mr-25" />
              {{ stepDuration(idx) }}
            </span>
            <span
              v-else
              class="occ-tl-duration occ-tl-duration--active"
              :style="{ color: entryColor(log), borderColor: entryColor(log) + '50', backgroundColor: entryColor(log) + '12' }"
            >
              <span class="occ-tl-pulse" :style="{ backgroundColor: entryColor(log) }" />
              {{ stepDuration(idx) }} · em andamento
            </span>
          </div>

          <div v-if="isDestination(log) && log.involved_name" class="occ-tl-transition">
            <feather-icon icon="UserIcon" size="11" class="mr-25" />
            {{ $t('occurrenceTimeline.forInvolvedPrefix') + ' ' }}<em>{{ log.involved_name }}</em>
          </div>

          <div v-if="isTechnicalDecision(log) && decisionScope(log)" class="occ-tl-transition">
            <feather-icon :icon="log.occurrence_involved_id ? 'UserIcon' : 'FileTextIcon'" size="11" class="mr-25" />
            {{ decisionScope(log) }}
          </div>

          <div v-if="isRedirect(log)" class="occ-tl-transition">
            <feather-icon icon="TruckIcon" size="11" class="mr-25" />
            <span v-if="log.direction === 'out'">
              {{ $t('occurrenceTimeline.redirectedOutPrefix') }}
              <em>{{ log.team_name || '—' }}</em>
              {{ $t('occurrenceTimeline.redirectedToProtocol', { protocol: log.other_protocol || '—' }) }}
            </span>
            <span v-else>
              {{ $t('occurrenceTimeline.redirectedInPrefix') }}
              <em>{{ log.team_name || '—' }}</em>
              {{ $t('occurrenceTimeline.redirectedFromProtocol', { protocol: log.other_protocol || '—' }) }}
            </span>
          </div>

          <div v-if="(isTechnicalDecision(log) || isAutoClassification(log)) && log.new_classification" class="occ-tl-classification-change">
            <span v-if="log.previous_classification" class="occ-tl-cls-chip">
              <i class="occ-tl-cls-dot" :style="{ backgroundColor: log.previous_classification.hex_color }" />
              {{ log.previous_classification.color_label }}
            </span>
            <feather-icon v-if="log.previous_classification" icon="ArrowRightIcon" size="11" class="mx-25" />
            <span class="occ-tl-cls-chip">
              <i class="occ-tl-cls-dot" :style="{ backgroundColor: log.new_classification.hex_color }" />
              {{ log.new_classification.color_label }}
            </span>
          </div>

          <div v-if="isAutoClassification(log) && autoClassificationDetail(log)" class="occ-tl-obs">
            {{ autoClassificationDetail(log) }}
          </div>

          <div v-if="log.user" class="occ-tl-meta">
            <feather-icon icon="UserIcon" size="11" class="mr-25" />
            {{ log.user.name }}
          </div>
          <div v-if="!isDestination(log) && !isTechnicalDecision(log) && log.from_step" class="occ-tl-transition">
            <feather-icon icon="CornerDownRightIcon" size="11" class="mr-25" />
            {{ 'Encaminhado de ' }}<em>{{ stepLabel(log.from_step) }}</em>
          </div>
          <div v-if="log.observation" class="occ-tl-obs">
            "{{ log.observation }}"
          </div>
          <div v-if="isTechnicalDecision(log) && log.details" class="occ-tl-obs">
            {{ log.details }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { BSpinner } from 'bootstrap-vue'

export default {
  name: 'OccurrenceTimeline',
  components: { BSpinner },
  props: {
    occurrenceId: {
      type: [Number, String],
      required: true,
    },
  },
  data() {
    return {
      logs: [],
      loading: false,
      now: new Date(),
      ticker: null,
    }
  },
  mounted() {
    this.fetchLogs()
  },
  beforeDestroy() {
    clearInterval(this.ticker)
  },
  methods: {
    async fetchLogs() {
      this.loading = true
      try {
        const res = await this.$http.get(
          this.$store.state.api + `occurrences/${this.occurrenceId}/step-logs`
        )
        this.logs = res.data.data || res.data || []
      } catch (e) {
        this.logs = []
      } finally {
        this.loading = false
        this.syncTicker()
      }
    },

    // O relógio só roda enquanto há etapa em andamento: ocorrência finalizada não tem o que contar.
    syncTicker() {
      const running = this.logs.some((log, idx) => this.isCurrentStep(idx))

      if (running && !this.ticker) {
        this.ticker = setInterval(() => { this.now = new Date() }, 1000)
      } else if (!running && this.ticker) {
        clearInterval(this.ticker)
        this.ticker = null
      }
    },

    // Índice da próxima entrada do tipo "step" a partir de idx (pula
    // eventos pontuais como destino/decisão técnica, que não fecham/abrem
    // etapa nenhuma).
    nextStepIndex(idx) {
      for (let i = idx + 1; i < this.logs.length; i++) {
        if (!this.isPunctualEntry(this.logs[i])) return i
      }
      return null
    },

    // Etapa em que a ocorrência está agora (a última, enquanto não foi finalizada).
    isCurrentStep(idx) {
      return !this.isPunctualEntry(this.logs[idx])
        && !this.isFinalStep(this.logs[idx])
        && this.nextStepIndex(idx) === null
    },

    isFinalStep(log) {
      return log.type === 'step' && log.to_step === 'finalizada'
    },

    isPunctualEntry(log) {
      return log.type === 'destination' || log.type === 'technical_decision' || log.type === 'redirect' || log.type === 'classification_auto'
    },

    isDestination(log) {
      return log.type === 'destination'
    },

    isTechnicalDecision(log) {
      return log.type === 'technical_decision'
    },

    isRedirect(log) {
      return log.type === 'redirect'
    },

    isAutoClassification(log) {
      return log.type === 'classification_auto'
    },

    // O que originou a classificação automática: o motivo escolhido ou a resposta do interrogatório.
    autoClassificationDetail(log) {
      if (log.source === 'interrogatory') {
        if (!log.question) return ''
        return log.alternative
          ? this.$t('occurrenceTimeline.classificationAuto.questionAnswer', { question: log.question, answer: log.alternative })
          : this.$t('occurrenceTimeline.classificationAuto.question', { question: log.question })
      }

      return log.reason_name
        ? this.$t('occurrenceTimeline.classificationAuto.reason', { reason: log.reason_name })
        : ''
    },

    decisionIcon(fixedKey) {
      const icons = {
        medical_guidance: 'BookOpenIcon',
        vehicle_request: 'TruckIcon',
        team_request: 'TruckIcon',
        support_request: 'PhoneCallIcon',
        telemedicine: 'MonitorIcon',
        teleservice: 'MonitorIcon',
        change_classification: 'FlagIcon',
      }
      return icons[fixedKey] || 'ZapIcon'
    },

    requestStateKey(status) {
      return { 1: 'pend', 2: 'ok', 3: 'cancel' }[status] || 'pend'
    },

    requestStateLabel(status) {
      return { 1: 'pendente', 2: 'atendida', 3: 'cancelada' }[status] || 'pendente'
    },

    // De quem é a decisão: do envolvido (com o nome) ou da ocorrência. Só para classificação, que existe nos dois níveis.
    decisionScope(log) {
      if (log.occurrence_involved_id) {
        return log.involved_name
          ? this.$t('occurrenceTimeline.scopeInvolved', { name: log.involved_name })
          : this.$t('occurrenceTimeline.scopeInvolvedNoName')
      }

      return log.decision_fixed_key === 'change_classification' ? this.$t('occurrenceTimeline.scopeOccurrence') : null
    },

    entryLabel(log) {
      if (log.type === 'destination') {
        return log.establishment
          ? this.$t('occurrenceTimeline.destinationDefinedWithName', { establishment: log.establishment })
          : this.$t('occurrenceTimeline.destinationDefined')
      }
      if (log.type === 'technical_decision') {
        return log.decision_name || '—'
      }
      if (log.type === 'classification_auto') {
        return this.$t(`occurrenceTimeline.classificationAuto.title.${log.source}`)
      }
      if (log.type === 'redirect') {
        return log.direction === 'out'
          ? this.$t('occurrenceTimeline.redirectedOutLabel')
          : this.$t('occurrenceTimeline.redirectedInLabel')
      }
      return this.stepLabel(log.to_step)
    },

    entryColor(log) {
      if (log.type === 'destination') return '#DB2777'
      if (log.type === 'technical_decision') return this.decisionColor(log.decision_fixed_key)
      if (log.type === 'redirect') return '#D97706'
      if (log.type === 'classification_auto') return (log.new_classification && log.new_classification.hex_color) || '#dc2626'
      return this.stepColor(log.to_step)
    },

    decisionColor(fixedKey) {
      const colors = {
        medical_guidance: '#2563eb',
        vehicle_request: '#16a34a',
        team_request: '#16a34a',
        support_request: '#ea580c',
        telemedicine: '#7c3aed',
        teleservice: '#7c3aed',
        change_classification: '#dc2626',
      }
      return colors[fixedKey] || '#6b7280'
    },

    stepDuration(idx) {
      const from = new Date(this.logs[idx].created_at)
      const nextIdx = this.nextStepIndex(idx)
      const to = nextIdx !== null
        ? new Date(this.logs[nextIdx].created_at)
        : this.now

      const totalSeconds = Math.max(0, Math.floor((to - from) / 1000))
      const days    = Math.floor(totalSeconds / 86400)
      const hours   = Math.floor((totalSeconds % 86400) / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60

      const parts = []
      if (days > 0)    parts.push(`${days} dia${days > 1 ? 's' : ''}`)
      if (hours > 0)   parts.push(`${hours} hora${hours > 1 ? 's' : ''}`)
      if (minutes > 0) parts.push(`${minutes} minuto${minutes > 1 ? 's' : ''}`)
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds} segundo${seconds !== 1 ? 's' : ''}`)

      if (parts.length === 1) return parts[0]
      return parts.slice(0, -1).join(', ') + ' e ' + parts[parts.length - 1]
    },

    stepLabel(step) {
      const labels = {
        solicita: 'Solicitação',
        triagem: 'Triagem',
        regulacao: 'Atendimento',
        despacha: 'Despacho',
        em_atendimento: 'Em Atendimento',
        encerrado: 'Encerrado',
      }
      if (labels[step]) return labels[step]
      if (!step) return '—'
      return step.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
    },

    stepColor(step) {
      const colors = {
        solicita: '#6B7280',
        triagem: '#F59E0B',
        regulacao: '#2563EB',
        despacha: '#7C3AED',
        em_atendimento: '#0891B2',
        encerrado: '#16A34A',
      }
      return colors[step] || '#6B7280'
    },

    dotStyle(log, isCurrent) {
      const color = this.entryColor(log)
      return isCurrent
        ? { backgroundColor: color, borderColor: color }
        : { backgroundColor: '#fff', borderColor: color }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—'
      return new Date(dateStr).toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.occ-tl-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  color: #b9b9c3;
  font-size: 13px;
  gap: 6px;
}

.occ-tl-list {
  display: flex;
  flex-direction: column;
}

.occ-tl-item {
  display: flex;
  gap: 14px;
}

/* ── Trilho ── */
.occ-tl-rail {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 16px;
  flex-shrink: 0;
  padding-top: 2px;
}

.occ-tl-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2.5px solid;
  flex-shrink: 0;
  transition: background 0.2s;
}

.occ-tl-dot--current {
  box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.08);
}

.occ-tl-connector {
  flex: 1;
  border-left: 2px dashed;
  min-height: 16px;
  margin-top: 5px;
  margin-bottom: 5px;
}

/* ── Corpo ── */
.occ-tl-body {
  flex: 1;
  padding-bottom: 22px;
  min-width: 0;
}

.occ-tl-body--last {
  padding-bottom: 4px;
}

.occ-tl-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 4px;
}

.occ-tl-step {
  font-size: 13px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.2;
}

.occ-tl-date {
  font-size: 11px;
  color: #b9b9c3;
  white-space: nowrap;
  flex-shrink: 0;
  padding-top: 1px;
}

/* ── Duração ── */
.occ-tl-duration-row {
  display: flex;
  align-items: center;
}

.occ-tl-duration {
  display: inline-flex;
  align-items: center;
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid;
  white-space: nowrap;
}

.occ-tl-duration--done {
  color: #b9b9c3;
  border-color: #ebe9f1;
  background: #f8f7ff;
}

.occ-tl-duration--active {
  /* color/border/bg via inline style */
  font-weight: 700;
}

.occ-tl-pulse {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-right: 6px;
  animation: tl-pulse 1.6s ease-in-out infinite;
}

@keyframes tl-pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50%       { opacity: 0.4; transform: scale(0.7); }
}

.occ-tl-meta {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #6e6b7b;
  margin-bottom: 3px;
}

.occ-tl-transition {
  display: flex;
  align-items: center;
  font-size: 11px;
  color: #b9b9c3;
  margin-bottom: 3px;
}

.occ-tl-obs {
  margin-top: 6px;
  padding: 7px 10px;
  background: #f8f7ff;
  border-left: 2px solid #7367f0;
  border-radius: 0 6px 6px 0;
  font-size: 12px;
  font-style: italic;
  color: #6e6b7b;
}

.occ-tl-state {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 8px;
  border-radius: 10px;
  font-size: 10px;
  font-weight: 700;
  text-transform: none;
  letter-spacing: normal;
  vertical-align: middle;
}
.occ-tl-state--pend   { background: #fef3c7; color: #92400e; }
.occ-tl-state--ok     { background: #dcfce7; color: #166534; }
.occ-tl-state--cancel { background: #fee2e2; color: #991b1b; }

.occ-tl-classification-change {
  display: flex;
  align-items: center;
  font-size: 12px;
  margin-bottom: 3px;
}

.occ-tl-cls-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
  color: #6e6b7b;
}

.occ-tl-cls-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

/* dark */
.dark-layout .occ-tl-dot {
  background-color: #283046 !important;
}

.dark-layout .occ-tl-duration--done {
  background: #1c2333;
  border-color: #3b4253;
  color: #676d7d;
}

.dark-layout .occ-tl-meta {
  color: #d0d2d6;
}

.dark-layout .occ-tl-obs {
  background: #1c2333;
  color: #d0d2d6;
}
</style>
